/**
 * Input aggregation: keyboard, mouse (pointer-locked), and gamepad, exposed as
 * a stable per-frame snapshot so gameplay never touches raw DOM events.
 *
 * Edge queries (`pressed`, `released`) are valid only during the frame in which
 * the transition happened — read them in update(), not fixedUpdate().
 */

export const ACTIONS = {
  forward: ['KeyW', 'ArrowUp'],
  back: ['KeyS', 'ArrowDown'],
  left: ['KeyA', 'ArrowLeft'],
  right: ['KeyD', 'ArrowRight'],
  jump: ['Space'],
  crouch: ['ControlLeft', 'KeyC'],
  prone: ['KeyZ'],
  sprint: ['ShiftLeft'],
  reload: ['KeyR'],
  use: ['KeyF'],
  melee: ['KeyV'],
  leanLeft: ['KeyQ'],
  leanRight: ['KeyE'],
  swapWeapon: ['Digit1', 'Digit2', 'Tab'],
  grenade: ['KeyG'],
  flashlight: ['KeyT'],
  pause: ['Escape'],
};

export class Input {
  constructor(canvas, config) {
    this.canvas = canvas;
    this.config = config;

    this.down = new Set(); // codes currently held
    this._pressed = new Set(); // went down this frame
    this._released = new Set(); // went up this frame
    this._pendingDown = new Set();
    this._pendingUp = new Set();

    /** Accumulated pointer delta for this frame, in radians after sensitivity. */
    this.look = { x: 0, y: 0 };
    this._rawLook = { x: 0, y: 0 };
    this.wheel = 0;
    this._pendingWheel = 0;

    this.pointerLocked = false;
    this.enabled = true;
    /** Set true by capture mode so scripted shots aren't fought by real input. */
    this.frozen = false;

    this.gamepadIndex = null;
    this.stick = { moveX: 0, moveY: 0, lookX: 0, lookY: 0 };
    // Per-axis rest calibration for the gamepad. See _axisLive.
    this._axisRest = [];
    this._axisLive_ = [];
    this._axisPad = -1;

    /**
     * Optional hook run at the very end of beginFrame(), after the edge sets and
     * the gamepad snapshot are built but BEFORE any fixedUpdate reads them.
     * `xr` installs itself here so headset controllers land in the same latch as
     * the keyboard: motion controllers are not exposed through
     * navigator.getGamepads(), and a poll from update() would be a frame late to
     * the movement machine, which latches in fixedUpdate.
     */
    this.auxPoll = null;

    this._bound = {
      keydown: this._onKeyDown.bind(this),
      keyup: this._onKeyUp.bind(this),
      mousedown: this._onMouseDown.bind(this),
      mouseup: this._onMouseUp.bind(this),
      mousemove: this._onMouseMove.bind(this),
      wheel: this._onWheel.bind(this),
      lockchange: this._onLockChange.bind(this),
      blur: this._onBlur.bind(this),
      contextmenu: (e) => e.preventDefault(),
    };
  }

  attach() {
    addEventListener('keydown', this._bound.keydown);
    addEventListener('keyup', this._bound.keyup);
    addEventListener('mousedown', this._bound.mousedown);
    addEventListener('mouseup', this._bound.mouseup);
    addEventListener('mousemove', this._bound.mousemove);
    addEventListener('wheel', this._bound.wheel, { passive: true });
    addEventListener('blur', this._bound.blur);
    document.addEventListener('pointerlockchange', this._bound.lockchange);
    this.canvas.addEventListener('contextmenu', this._bound.contextmenu);
  }

  detach() {
    removeEventListener('keydown', this._bound.keydown);
    removeEventListener('keyup', this._bound.keyup);
    removeEventListener('mousedown', this._bound.mousedown);
    removeEventListener('mouseup', this._bound.mouseup);
    removeEventListener('mousemove', this._bound.mousemove);
    removeEventListener('wheel', this._bound.wheel);
    removeEventListener('blur', this._bound.blur);
    document.removeEventListener('pointerlockchange', this._bound.lockchange);
    this.canvas.removeEventListener('contextmenu', this._bound.contextmenu);
  }

  requestPointerLock() {
    // Chrome returns a promise that rejects if the document is not eligible
    // (headless capture, an iframe, a lock request too soon after an exit).
    // An unhandled rejection there shows up as a page error in the harness, so
    // swallow it: failing to lock is not a game error.
    try {
      const p = this.canvas.requestPointerLock?.();
      if (p && typeof p.catch === 'function') p.catch(() => {});
    } catch {
      /* not eligible — keep running unlocked */
    }
  }

  _onKeyDown(e) {
    if (!this.enabled) return;
    if (e.repeat) return;
    // Let devtools/refresh through; swallow everything else the game binds.
    if (!e.metaKey && !e.ctrlKey) e.preventDefault();
    this._pendingDown.add(e.code);
  }

  _onKeyUp(e) {
    if (!this.enabled) return;
    this._pendingUp.add(e.code);
  }

  _onMouseDown(e) {
    if (!this.enabled) return;
    if (!this.pointerLocked && e.button === 0) this.requestPointerLock();
    this._pendingDown.add(`Mouse${e.button}`);
  }

  _onMouseUp(e) {
    if (!this.enabled) return;
    this._pendingUp.add(`Mouse${e.button}`);
  }

  _onMouseMove(e) {
    if (!this.enabled || !this.pointerLocked || this.frozen) return;
    // movementX/Y is already relative and unaffected by cursor clamping.
    this._rawLook.x += e.movementX ?? 0;
    this._rawLook.y += e.movementY ?? 0;
  }

  _onWheel(e) {
    if (!this.enabled) return;
    this._pendingWheel += Math.sign(e.deltaY);
  }

  _onLockChange() {
    this.pointerLocked = document.pointerLockElement === this.canvas;
    if (!this.pointerLocked) this._onBlur();
  }

  /** Losing focus must release every held key, or the player runs forever. */
  _onBlur() {
    for (const code of this.down) this._pendingUp.add(code);
    this._rawLook.x = 0;
    this._rawLook.y = 0;
  }

  beginFrame() {
    this._pressed.clear();
    this._released.clear();

    for (const code of this._pendingDown) {
      if (!this.down.has(code)) {
        this.down.add(code);
        this._pressed.add(code);
      }
    }
    for (const code of this._pendingUp) {
      if (this.down.delete(code)) this._released.add(code);
    }
    this._pendingDown.clear();
    this._pendingUp.clear();

    const s = this.config.sensitivity;
    this.look.x = this.frozen ? 0 : this._rawLook.x * s;
    this.look.y = this.frozen ? 0 : this._rawLook.y * s * (this.config.invertY ? -1 : 1);
    this._rawLook.x = 0;
    this._rawLook.y = 0;

    this.wheel = this._pendingWheel;
    this._pendingWheel = 0;

    this._pollGamepad();
    this.auxPoll?.(this);
  }

  endFrame() {}

  /**
   * Drive a bound code from an external device, with the same edge semantics a
   * real key gets. Used by `xr` to map controller buttons onto the existing
   * action table instead of forking every gameplay call site.
   */
  setVirtual(code, held) {
    if (held) {
      if (!this.down.has(code)) {
        this.down.add(code);
        this._pressed.add(code);
      }
    } else if (this.down.delete(code)) {
      this._released.add(code);
    }
  }

  /**
   * Pick a device to read.
   *
   * `navigator.getGamepads()` returns EVERY HID the browser decided looks
   * gamepad-shaped, which on a real desk means sim wheels, pedal boards, HOTAS
   * sticks, button boxes and racing rigs — not just controllers. Taking the
   * first non-null entry (which is what this did) hands the look axes to
   * whichever of those enumerated first, and a pedal at rest reads -1 or +1
   * rather than 0, so the view spins forever at full deflection with nothing
   * touched.
   *
   * Two filters, because either alone is escapable:
   *   1. `mapping === 'standard'` — the pad has told us its axes mean what the
   *      standard layout says they mean. Wheels and flight gear report ''.
   *   2. a rest-value check per axis (see _axisLive) — a "standard" pad with a
   *      dead stick still cannot drive the camera until the axis has been seen
   *      to actually move.
   */
  _pickGamepad(pads) {
    if (this.gamepadIndex !== null) return pads[this.gamepadIndex] ?? null;
    for (let i = 0; i < pads.length; i++) {
      const p = pads[i];
      if (!p || p.connected === false) continue;
      if (p.mapping !== 'standard') continue;
      if (!p.axes || p.axes.length < 4) continue;
      return p;
    }
    return null;
  }

  /**
   * An axis is trusted only once it has been observed to MOVE.
   *
   * A thumbstick at rest reads ~0, so it goes live on its first real
   * deflection and nothing is lost. An axis that boots pegged at ±1 and stays
   * there is a resting pedal or an uncentred hat, and is held at zero until it
   * proves otherwise. This is what stops a connected sim rig from flying the
   * camera around the map.
   */
  _axisLive(pad, i) {
    const rest = this._axisRest;
    const live = this._axisLive_;
    if (this._axisPad !== pad.index) {
      this._axisPad = pad.index;
      rest.length = 0;
      live.length = 0;
    }
    const v = pad.axes[i] ?? 0;
    if (rest[i] === undefined) {
      rest[i] = v;
      live[i] = Math.abs(v) < 0.5; // already near centre: trust it immediately
      if (!live[i]) {
        console.info(
          `[input] gamepad "${pad.id}" axis ${i} rests at ${v.toFixed(2)} — ignoring it until it moves`
        );
      }
    }
    if (!live[i] && Math.abs(v - rest[i]) > 0.08) live[i] = true;
    return live[i] ? v : 0;
  }

  _pollGamepad() {
    const pads = navigator.getGamepads?.() ?? [];
    const pad = this._pickGamepad(pads);
    if (!pad) {
      this.stick.moveX = this.stick.moveY = this.stick.lookX = this.stick.lookY = 0;
      return;
    }
    const dz = (v) => (Math.abs(v) < 0.16 ? 0 : (v - Math.sign(v) * 0.16) / 0.84);
    this.stick.moveX = dz(this._axisLive(pad, 0));
    this.stick.moveY = dz(this._axisLive(pad, 1));
    // Cubic response curve on the look stick — fine aim near centre, fast flicks at the edge.
    const curve = (v) => Math.sign(v) * Math.abs(v) ** 2.4;
    this.stick.lookX = curve(dz(this._axisLive(pad, 2)));
    this.stick.lookY = curve(dz(this._axisLive(pad, 3)));
  }

  /** True while any key bound to `action` is held. */
  action(name) {
    const codes = ACTIONS[name];
    if (!codes) return false;
    for (const c of codes) if (this.down.has(c)) return true;
    return false;
  }

  actionPressed(name) {
    const codes = ACTIONS[name];
    if (!codes) return false;
    for (const c of codes) if (this._pressed.has(c)) return true;
    return false;
  }

  held(code) {
    return this.down.has(code);
  }

  pressed(code) {
    return this._pressed.has(code);
  }

  released(code) {
    return this._released.has(code);
  }

  get fire() {
    return this.down.has('Mouse0');
  }

  get firePressed() {
    return this._pressed.has('Mouse0');
  }

  get ads() {
    return this.down.has('Mouse2');
  }

  /** Normalised WASD + left-stick movement, clamped to the unit disc so
   *  diagonals aren't faster than cardinals. */
  moveVector(out = { x: 0, y: 0 }) {
    let x = (this.action('right') ? 1 : 0) - (this.action('left') ? 1 : 0);
    let y = (this.action('forward') ? 1 : 0) - (this.action('back') ? 1 : 0);
    x += this.stick.moveX;
    y -= this.stick.moveY;
    const len = Math.hypot(x, y);
    if (len > 1) {
      x /= len;
      y /= len;
    }
    out.x = x;
    out.y = y;
    return out;
  }
}
